import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import {
  Application,
  Assets,
  Sprite,
  Container,
  Graphics,
  Text,
  TextStyle,
  Texture,
  Point,
} from "pixi.js";
import { initDevtools } from "@pixi/devtools";
import { gsap } from "gsap";

import pinPink from "../../../../assets/images/pinPink.svg";
import pinYellow from "../../../../assets/images/pinYellow.svg";
import magnetPink from "../../../../assets/images/magnetPink.svg";
import magnetYellow from "../../../../assets/images/magnetYellow.svg";
import pinboardBgImage from "../../../../assets/images/pinboard-bg.png";
import whiteboardBgImage from "../../../../assets/images/whiteboard-bg.png";

const boardWidth = 1120;
const boardHeight = 680;
const LEVEL_KEYS = ["rookie", "intermediate", "advanced"];

/* -------------------- helpers over new data shape -------------------- */
const forEachClue = (mission, cb) => {
  if (!mission?.coins) return;
  for (const key of LEVEL_KEYS) {
    const list = mission.coins[key];
    if (Array.isArray(list)) list.forEach(cb);
  }
};

const hasValidPos = (clue) =>
  clue?.position &&
  Number.isFinite(clue.position.x) &&
  Number.isFinite(clue.position.y);

const getPinnedImageClues = (mission) => {
  const out = [];
  forEachClue(mission, (clue) => {
    if (clue?.type === "image" && clue.pinned === true && hasValidPos(clue)) {
      out.push(clue);
    }
  });
  return out;
};

const findPinnedClue = (missions, missionId, clueId) => {
  // Prefer the provided missionId (avoids "mission-1 first" problem)
  if (missionId) {
    const m = missions.find((x) => x.id === missionId);
    if (m) {
      let found = null;
      forEachClue(m, (c) => {
        if (!found && c.id === clueId && c.pinned === true) found = c;
      });
      if (found) return { missionId: m.id, mission: m, clue: found };
    }
  }
  // Fallback (not ideal with duplicate ids, but used only if missionId missing)
  for (const m of missions) {
    let found = null;
    forEachClue(m, (c) => {
      if (!found && c.id === clueId && c.pinned === true) found = c;
    });
    if (found) return { missionId: m.id, mission: m, clue: found };
  }
  return null;
};

/* ------------------------- spotlight overlay ------------------------- */
function createSpotlightOverlayTexture(
  width,
  height,
  spotX,
  spotY,
  spotRadius,
  blur
) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "rgba(0,0,0,0.6)";
  ctx.fillRect(18, 45, width, height);

  ctx.save();
  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath();
  ctx.arc(spotX, spotY, spotRadius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.shadowColor = "black";
  ctx.shadowBlur = blur;
  ctx.fill();
  ctx.restore();

  return Texture.from(canvas);
}

/* --------------------- svg path → sampled points --------------------- */
function sampleSvgPathPoints(d, samples = 800) {
  const svgNS = "http://www.w3.org/2000/svg";
  const path = document.createElementNS(svgNS, "path");
  path.setAttribute("d", d);

  const total = path.getTotalLength();
  const pts = [];
  for (let i = 0; i <= samples; i++) {
    const p = path.getPointAtLength((i / samples) * total);
    pts.push({ x: p.x, y: p.y });
  }
  return { points: pts, totalLength: total };
}

/* ----------------------- animated stroke drawing ---------------------- */
function createAnimatedSvgStroke(app, sampled, opts = {}) {
  const {
    x = 0,
    y = 0,
    scale = 1,
    rotation = 0,
    lineWidth = 4,
    color = "0xffc01d",
    alpha = 1,
    duration = 1.1,
    ease = "power2.out",
    blendMode = "darken",
  } = opts;

  const g = new Graphics();
  g.position.set(x, y);
  g.scale.set(scale);
  g.rotation = rotation;
  g.zIndex = 2000;
  g.blendMode = blendMode;
  app.stage.addChild(g);
  app.stage.sortableChildren = true;

  const state = { i: 0 };
  const max = sampled.points.length - 1;

  const draw = () => {
    g.clear();
    const p0 = sampled.points[0];
    g.moveTo(p0.x, p0.y);
    for (let k = 1; k <= state.i && k < sampled.points.length; k++) {
      const p = sampled.points[k];
      g.lineTo(p.x, p.y);
    }
    g.stroke({ width: lineWidth, color, alpha });
  };

  draw();

  const tween = gsap.to(state, {
    i: max,
    duration,
    ease,
    onUpdate: draw,
  });

  return {
    destroy() {
      tween.kill();
      g.destroy();
    },
  };
}

/* ---------------- create a single image clue sprite ---------------- */
const createImageClue = (
  container,
  clue,
  textures,
  onClick,
  isFlashes,
  flashAnimations,
  scopeKey,
  isClickable = true
) => {
  const flashMap =
    flashAnimations && typeof flashAnimations.get === "function"
      ? flashAnimations
      : null;

  const imageContainer = new Container();
  // position imageContainer at the stored clue position (these are stage coords)
  imageContainer.x = clue.position.x;
  imageContainer.y = clue.position.y;
  imageContainer.label = `${clue.clueType + clue.id}`;
  imageContainer.angle = clue.rotation || 0;
  imageContainer.zIndex = clue.zIndex || 100;
  imageContainer.eventMode = isClickable ? "static" : "none";
  imageContainer.cursor = isClickable ? "pointer" : "default";

  // handle pointertap — compute the visual center of the image in stage coords
  if (isClickable && typeof onClick === "function") {
    imageContainer.on("pointertap", () => {
      const centerGlobal = imageContainer.toGlobal(
        new Point(0, 0),
        undefined,
        true
      );
      onClick(clue, { x: centerGlobal.x, y: centerGlobal.y });
    });
  }
  container.addChild(imageContainer);

  if (clue.src) {
    Assets.load(clue.src)
      .then((texture) => {
        const sprite = new Sprite(texture);
        // center the sprite inside imageContainer so visual center is at (0,0)
        sprite.anchor.set(0.5, 0.5);
        // put sprite at container origin; because imageContainer is already at clue.position,
        // imageContainer.toGlobal(new Point(0,0)) will be the sprite's visual center.
        sprite.position.set(0, 0);

        imageContainer.addChild(sprite);

        createFlashAnimation(sprite);
      })
      .catch(() => {});
  }

  function createFlashAnimation(target) {
    if (!isFlashes || !clue.pinned || !flashMap) return;
    const key = `${scopeKey}_img`;
    const existing = flashMap.get(key);
    if (existing) existing.kill();
    const anim = gsap.to(target, {
      alpha: 0.5,
      duration: 1,
      repeat: 1,
      yoyo: true,
      ease: "power2.inOut",
    });
    flashMap.set(key, anim);
  }

  // pin/magnet sprite
  if (clue.isPin === true) {
    const pinTexture =
      clue.pinColor === "pink"
        ? textures.secondaryPinTexture
        : textures.primaryPinTexture;
    const pin = new Sprite(pinTexture);
    pin.anchor.set(0.5, 0.5);

    // pin offset relative to imageContainer center
    pin.x = clue.pinPosition?.x ?? 20;
    pin.y = clue.pinPosition?.y ?? 15;
    pin.zIndex = 100;
    imageContainer.addChild(pin);

    if (isFlashes && clue.pinned && flashMap) {
      const key = `${scopeKey}_pin`;
      const existing = flashMap.get(key);
      if (existing) existing.kill();
      const anim = gsap.to(pin, {
        alpha: 0.5,
        duration: 1,
        repeat: 1,
        yoyo: true,
        ease: "power2.inOut",
      });
      flashMap.set(key, anim);
    }
  }
};

/* ------------------------------ Board ------------------------------ */
const Board = ({
  marginTop = "0",
  isClickable = true,
  isFlashes = false,
  selectedClueId = null,
  selectedMissionId = null, // optional but preferred to avoid duplicate-id ambiguity
  setSelectedClueId,
  setSelectedMissionId,
}) => {
  const containerRef = useRef(null);
  const appRef = useRef(null);
  const contentContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const strokeRef = useRef(null);
  const flashAnimationsRef = useRef(new Map());
  const texturesRef = useRef(null);
  const appReadyRef = useRef(false); // Add this to track app readiness

  const [position, setPosition] = useState({ x: 0, y: 0 });

  const missions = useSelector((state) => state.pinclue.missions);

  const settings = useSelector((state) => state.settings);
  const boardStyle = settings.clueBoardStyle || "pinboard";

  // force re-render when any pinned/position/type changes (fixes "needs refresh to show")
  const pinsVersion = useMemo(() => {
    try {
      return JSON.stringify(
        (missions || []).map((m) => ({
          id: m.id,
          coins: LEVEL_KEYS.flatMap((lv) =>
            (m.coins?.[lv] || []).map((c) => ({
              id: c.id,
              pinned: !!c.pinned,
              x: c.position?.x ?? null,
              y: c.position?.y ?? null,
              type: c.type || null,
            }))
          ),
        }))
      );
    } catch {
      return String(Date.now());
    }
  }, [missions]);

  // Path used for focus stroke
  const svgPathD =
    "M94.4661 131C59.0598 121.932 45.3541 134.967 17.3717 102.663C-10.6107 70.3582 -2.04464 23.8851 56.7755 5.18246C115.596 -13.5201 154.428 54.4893 120.164 97.562C92.7529 132.02 45.9252 131.945 25.9378 127.599";
  const sampledPath = sampleSvgPathPoints(svgPathD, 900);

  const handleClueClick = useCallback(
    (clue, pos, missionId) => {
      if (!isClickable) return;
      setPosition(pos);
      setSelectedMissionId?.(missionId); // keep composite identity
      setSelectedClueId?.((prev) => (prev === clue.id ? null : clue.id));
    },
    [isClickable, setSelectedClueId, setSelectedMissionId]
  );

  // Sync overlay position when external selection changes (use missionId+clueId if provided)
  useEffect(() => {
    if (!selectedClueId) return;
    const found = findPinnedClue(missions, selectedMissionId, selectedClueId);
    if (found?.clue?.position) {
      const clueCenter = {
        x: found.clue.position.x,
        y: found.clue.position.y,
      };
      setPosition(clueCenter);
      // if parent didn't pass missionId but we resolved it, inform parent
      if (!selectedMissionId && found.missionId) {
        setSelectedMissionId?.(found.missionId);
      }
    }
  }, [selectedClueId, selectedMissionId, missions, setSelectedMissionId]);

  // Textures
  const loadTextures = useCallback(async () => {
    if (texturesRef.current) return texturesRef.current;

    try {
      const [bgTexture, primaryPinTexture, secondaryPinTexture] =
        await Promise.all([
          Assets.load(
            boardStyle === "pinboard" ? pinboardBgImage : whiteboardBgImage
          ),
          Assets.load(boardStyle === "pinboard" ? pinYellow : magnetYellow),
          Assets.load(boardStyle === "pinboard" ? pinPink : magnetPink),
        ]);

      texturesRef.current = {
        bgTexture,
        primaryPinTexture,
        secondaryPinTexture,
        boardStyle,
      };

      return texturesRef.current;
    } catch (error) {
      console.error("Failed to load textures:", error);
      return null;
    }
  }, [boardStyle]);

  // Spotlight + stroke
  // Update the updateSpotlightAndStroke function in your Board component

  const updateSpotlightAndStroke = useCallback(
    (app) => {
      if (!appReadyRef.current || !app?.stage) return;

      // clean previous overlay/stroke
      if (overlayRef.current) {
        overlayRef.current.destroy();
        overlayRef.current = null;
      }
      if (strokeRef.current) {
        strokeRef.current.destroy();
        strokeRef.current = null;
      }

      //  find the selected clue first
      const found = findPinnedClue(missions, selectedMissionId, selectedClueId);
      const position = found?.clue?.position;

      //  allow system-driven highlight even when clicks are locked
      if (
        selectedClueId &&
        position &&
        Number.isFinite(position.x) &&
        Number.isFinite(position.y)
      ) {
        // overlay
        const overlayTex = createSpotlightOverlayTexture(
          boardWidth,
          boardHeight,
          position.x,
          position.y,
          57,
          44
        );
        const overlaySprite = new Sprite(overlayTex);
        overlaySprite.zIndex = 999;
        overlaySprite.alpha = 0.65;
        overlaySprite.eventMode = "none"; //  do not clear selection on click
        overlaySprite.cursor = "default";
        app.stage.addChild(overlaySprite);
        overlayRef.current = overlaySprite;

        // stroke ring (your sampledPath vars stay as-is)
        const svgCenterX = 67.5;
        const svgCenterY = 66.5;
        const scale = 0.9;

        strokeRef.current = createAnimatedSvgStroke(app, sampledPath, {
          x: position.x - svgCenterX * scale,
          y: position.y - svgCenterY * scale,
          scale,
          lineWidth: 4,
          color: 0xffc01d,
          alpha: 1,
          duration: 1.0,
          ease: "power2.out",
          blendMode: "darken",
        });

        app.stage.sortableChildren = true;
      }
    },
    [missions, selectedClueId, selectedMissionId]
  );

  // Render content: ONLY pinned image clues (with positions) + titles if any pinned
  const renderContent = useCallback(
    async (app) => {
      if (!appReadyRef.current || !app?.stage) return; // Check if app is ready

      const textures = await loadTextures();
      if (!textures) return; // Exit if textures failed to load

      if (!contentContainerRef.current) {
        contentContainerRef.current = new Container();
        contentContainerRef.current.zIndex = 30;
        app.stage.addChild(contentContainerRef.current);
      }

      const contentContainer = contentContainerRef.current;
      contentContainer.removeChildren();

      // kill existing flashes
      flashAnimationsRef.current.forEach((anim) => anim.kill());
      flashAnimationsRef.current.clear();

      app.stage.sortableChildren = true;

      // Preload all pinned image textures to avoid flicker
      const pinnedClues = missions.flatMap((m) => getPinnedImageClues(m));
      const imageLoadPromises = pinnedClues
        .filter((c) => !!c.src)
        .map((c) => Assets.load(c.src).catch(() => null));

      await Promise.allSettled(imageLoadPromises);

      missions.forEach((mission) => {
        const pinned = getPinnedImageClues(mission);
        pinned.forEach((clue) => {
          const scopeKey = `${mission.id}::${clue.id}`;
          createImageClue(
            contentContainer,
            clue,
            textures,
            (clickedClue, pos) => handleClueClick(clickedClue, pos, mission.id),
            isFlashes,
            flashAnimationsRef.current,
            scopeKey,
            isClickable
          );
        });
      });

      // Titles only for missions with at least one pinned
      missions.forEach((mission) => {
        const hasPinned = getPinnedImageClues(mission).length > 0;
        if (hasPinned && mission.position) {
          // Add position check
          const titleStyle = new TextStyle({
            fill: boardStyle === "pinboard" ? 0xffffff : 0xce2357,
            fontSize: 20,
            fontWeight: "400",
            fontFamily: "Permanent Marker, cursive",
            wordWrap: true,
            wordWrapWidth: 250,
            align: "center",
          });
          const titleText = new Text({
            text: mission.title,
            style: titleStyle,
          });
          titleText.resolution = 2; // sharper text
          titleText.anchor.set(0.5);
          titleText.position.set(mission.position.x, mission.position.y);
          contentContainer.addChild(titleText);
        }
      });
    },
    [missions, boardStyle, isFlashes, handleClueClick, loadTextures]
  );

  // Init
  const initializeScene = useCallback(
    async (app) => {
      if (!app?.stage) return;

      const textures = await loadTextures();
      if (!textures) return;

      app.stage.removeChildren();

      const bgSprite = new Sprite(textures.bgTexture);
      bgSprite.width = boardWidth;
      bgSprite.height = boardHeight;
      app.stage.addChild(bgSprite);

      appReadyRef.current = true; // Mark app as ready after background is loaded

      await renderContent(app);
      updateSpotlightAndStroke(app);
    },
    [loadTextures, renderContent, updateSpotlightAndStroke]
  );

  // Spotlight updates - only if app is ready
  useEffect(() => {
    if (appRef.current && appReadyRef.current) {
      updateSpotlightAndStroke(appRef.current);
    }
  }, [selectedClueId, selectedMissionId, position, updateSpotlightAndStroke]);

  // Re-render content when pins/positions/style/flash changes - only if app is ready
  useEffect(() => {
    if (appRef.current && appReadyRef.current) {
      renderContent(appRef.current);
    }
  }, [missions, pinsVersion, boardStyle, isFlashes, renderContent]);

  // Mount/unmount Pixi app
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    // Cleanup previous app
    if (appRef.current) {
      appReadyRef.current = false;
      appRef.current.destroy(true, { children: true });
      appRef.current = null;
      contentContainerRef.current = null;
      texturesRef.current = null;
    }

    const app = new Application();

    app
      .init({
        backgroundAlpha: 0,
        antialias: true,
        // autoDensity: true,
        width: boardWidth,
        height: boardHeight,
      })
      .then(() => {
        if (container && !appRef.current) {
          // Check if component is still mounted
          container.innerHTML = "";
          container.appendChild(app.canvas);
          appRef.current = app;
          initDevtools({ app });
          initializeScene(app).catch(console.error);
        }
      })
      .catch((error) => {
        console.error("Failed to initialize PIXI application:", error);
      });
    return () => {
      appReadyRef.current = false;

      if (strokeRef.current) {
        strokeRef.current.destroy();
        strokeRef.current = null;
      }
      if (overlayRef.current) {
        overlayRef.current.destroy();
        overlayRef.current = null;
      }

      flashAnimationsRef.current.forEach((animation) => animation.kill());
      flashAnimationsRef.current.clear();

      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
      }
      contentContainerRef.current = null;
      texturesRef.current = null;

      if (container) container.innerHTML = "";
    };
  }, []); // mount/unmount only

  return (
    <div
      ref={containerRef}
      style={{
        width: `${boardWidth}px`,
        height: `${boardHeight}px`,
        marginTop: `${marginTop}px`,
      }}
    />
  );
};

export default Board;
